import { Award, BarChart3 } from "lucide-react";

/**
 * Bloque compartido de benchmark por categoría (portal proveedor + demo público).
 * Muestra: score 0-100 prominente (+0-10 referencia), barra comparativa vs promedio
 * de la categoría, posición anónima, percentil y el distintivo "Mejor de su Categoría".
 */
export const CategoryBenchmark = ({ benchmark, demo = false }) => {
  if (!benchmark || benchmark.supplier_score == null) return null;
  const b = benchmark;
  const prefix = demo ? "demo-" : "";
  const earned = b.badge?.earned;
  const diff = +(b.supplier_score - (b.category_avg ?? 0)).toFixed(2);

  return (
    <div className="sketch-border bg-[var(--cream-soft)] p-5 sm:p-6 mb-6" data-testid={`${prefix}benchmark-block`}>
      <div className="font-hand text-lg sm:text-xl text-[var(--lala-blue)] flex items-center gap-2">
        <BarChart3 size={18} /> benchmark de tu categoría
      </div>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <h3 className="font-display text-xl sm:text-2xl mb-3">Cómo te comparas en {b.category}</h3>
        {earned && (
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold text-white uppercase tracking-wide"
            style={{ background: "#D4A437" }}
            data-testid={`${prefix}best-of-category-badge`}
          >
            <Award size={13} /> {b.badge.label}
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {/* Score 0-100 prominente */}
        <div className="bg-white rounded-xl p-4 border border-[var(--line)]">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Score total</div>
          <div className="font-display text-4xl sm:text-5xl text-[var(--lala-blue)] mt-1" data-testid={`${prefix}benchmark-score-100`}>
            {b.supplier_score_100.toFixed(1)}<span className="text-lg text-[var(--ink-soft)]"> /100</span>
          </div>
          <div className="text-xs text-[var(--ink-soft)] mt-1">{b.supplier_score.toFixed(2)} en escala 0–10</div>
        </div>

        {/* Comparativo vs promedio de la categoría */}
        <div className="bg-white rounded-xl p-4 border border-[var(--line)]" data-testid={`${prefix}benchmark-bars`}>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold mb-2">Tú vs promedio de {b.category}</div>
          <BenchBar label="Tu score" value={b.supplier_score} color="var(--lala-blue)" />
          <BenchBar label="Promedio categoría" value={b.category_avg} color="#9AA1BD" />
          {b.individual_target != null && (
            <BenchBar label="Meta acordada con Lala" value={b.individual_target} color="#FFC400" />
          )}
          <div className={`text-xs font-bold mt-2 ${diff >= 0 ? "text-emerald-700" : "text-red-700"}`} data-testid={`${prefix}benchmark-diff`}>
            {diff >= 0 ? "+" : ""}{diff.toFixed(2)} pts vs tu categoría
          </div>
          {b.target_gap != null && (
            <div className={`text-[11px] mt-1 ${b.target_gap <= 0 ? "text-emerald-700" : "text-amber-700"}`} data-testid={`${prefix}benchmark-target-gap`}>
              {b.target_gap <= 0 ? `Ya superaste tu meta por ${Math.abs(b.target_gap).toFixed(2)} pts.` : `Te faltan ${b.target_gap.toFixed(2)} pts para tu meta individual.`}
            </div>
          )}
        </div>

        {/* Posición anónima + percentil */}
        <div className="bg-white rounded-xl p-4 border border-[var(--line)]">
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-soft)] font-bold">Posición en tu categoría</div>
          <div className="font-display text-2xl sm:text-3xl mt-1" data-testid={`${prefix}benchmark-rank`}>
            N° {b.rank ?? "—"} <span className="text-base text-[var(--ink-soft)]">de {b.total_in_category}</span>
          </div>
          {b.percentile_label && (
            <div className="inline-flex items-center mt-2 px-2.5 py-1 rounded-full bg-[var(--lala-blue)] text-white text-xs font-bold" data-testid={`${prefix}benchmark-percentile`}>
              {b.percentile_label} de {b.category}
            </div>
          )}
          <p className="text-[11px] text-[var(--ink-soft)] mt-2 leading-snug">
            Ranking anónimo — nunca verás los nombres de otros proveedores.
          </p>
        </div>
      </div>

      {!earned && b.badge?.enabled && (
        <p className="text-xs text-[var(--ink-soft)] mt-3" data-testid={`${prefix}badge-hint`}>
          El distintivo <b className="text-[var(--ink)]">{b.badge.label}</b> se otorga al N° 1 de cada categoría
          con score global ≥ {b.badge.min_score.toFixed(1)}.
        </p>
      )}
    </div>
  );
};

function BenchBar({ label, value, color }) {
  const v = value ?? 0;
  return (
    <div className="mb-2">
      <div className="flex justify-between items-baseline text-xs font-semibold">
        <span>{label}</span>
        <span className="text-[var(--ink)]">{v.toFixed(2)}</span>
      </div>
      <div className="h-2 mt-0.5 rounded-full bg-[var(--cream-deep)]">
        <div className="h-2 rounded-full" style={{ width: `${Math.min((v / 10) * 100, 100)}%`, background: color }} />
      </div>
    </div>
  );
}

export default CategoryBenchmark;
